import { useState, useRef, useEffect, useMemo } from 'react'
import ChatWindow, { type Message } from './components/ChatWindow'
import MessageInput from './components/MessageInput'
import NavBar, { type Page } from './components/NavBar'
import SideNav from './components/SideNav'
import CandidatesPage from './components/CandidatesPage'
import ShortcutsPage from './components/ShortcutsPage'
import SceneListPage from './components/SceneListPage'
import SceneMapPage from './components/SceneMapPage'
import SheetTablePage from './components/SheetTablePage'
import DocPage from './components/DocPage'
import ModLogPage from './components/ModLogPage'
import SyncPage from './components/SyncPage'
import LoginPage from './components/LoginPage'
import AppLoadingView from './components/AppLoadingView'
import { streamChat, approveCandidate, skipCandidate, deleteCandidate, fetchMe, type HistoryMessage } from './api'
import { getToken, clearToken } from './auth'

let msgCounter = 0
function uid() { return String(++msgCounter) }

export default function App() {
  const bypassAuth = import.meta.env.VITE_BYPASS_AUTH === 'true'
  const [token,    setToken_]   = useState<string | null>(() => bypassAuth ? 'dev' : getToken())
  const [verified, setVerified] = useState<boolean>(bypassAuth)
  const [authError, setAuthError] = useState<string | null>(null)
  const [page,     setPage]     = useState<Page>('chat')
  const [visited,  setVisited]  = useState<Set<Page>>(new Set(['chat']))
  const [navOpen,  setNavOpen]  = useState(false)
  const [messages, setMessages] = useState<Message[]>([])
  const [loading,  setLoading]  = useState(false)
  const [error,    setError]    = useState<string | null>(null)
  const [warnings, setWarnings] = useState<string[]>([])
  const messagesRef = useRef<Message[]>([])
  messagesRef.current = messages
  const abortControllerRef = useRef<AbortController | null>(null)
  // Full Anthropic-format message history including tool calls/results — used as history on next turn
  const fullHistoryRef = useRef<HistoryMessage[]>([])

  const historySizeBytes = useMemo(() => {
    const src = fullHistoryRef.current.length > 0 ? fullHistoryRef.current : messages
    return JSON.stringify(src).length
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [messages])

  // Stale page tracking — pages in this set reload on next navigation
  const staleRef = useRef<Set<Page>>(new Set())
  const [refreshKeys, setRefreshKeys] = useState<Partial<Record<Page, number>>>({})

  function markStale(...pages: Page[]) {
    pages.forEach(p => staleRef.current.add(p))
  }

  // Pending candidate batch tracking — auto-send summary when all actioned
  const pendingCandidatesRef = useRef<Map<string, { status: 'pending' | 'approved' | 'skipped' | 'deleted'; label?: string }>>(new Map())

  useEffect(() => {
    if (bypassAuth || !token) return
    fetchMe().then((user) => {
      if (user) {
        setVerified(true)
      } else {
        clearToken()
        setToken_(null)
        setAuthError('403')
      }
    })
  }, [token])

  // Character-level streaming buffer for smooth text animation
  const charBufferRef  = useRef('')
  const activeAidRef   = useRef<string | null>(null)
  const charTimerRef   = useRef<ReturnType<typeof setInterval> | null>(null)

  function flushCharBuffer() {
    const remaining = charBufferRef.current
    const aid = activeAidRef.current
    if (!remaining || !aid) return
    charBufferRef.current = ''
    setMessages((prev) => prev.map((m) => m.id === aid ? { ...m, content: m.content + remaining } : m))
  }

  function stopCharTimer() {
    if (charTimerRef.current) {
      clearInterval(charTimerRef.current)
      charTimerRef.current = null
    }
  }

  function checkAndSendCandidateSummary() {
    const map = pendingCandidatesRef.current
    if (map.size === 0) return
    const entries = [...map.values()]
    if (entries.some(e => e.status === 'pending')) return
    const approvedLabels = entries.filter(e => e.status === 'approved').map(e => e.label).filter(Boolean)
    const skippedLabels  = entries.filter(e => e.status === 'skipped').map(e => e.label).filter(Boolean)
    const deletedLabels  = entries.filter(e => e.status === 'deleted').map(e => e.label).filter(Boolean)
    let msg: string
    if (map.size === 1) {
      const { status, label } = entries[0]
      const nameStr = label ? ` "${label}"` : ''
      if (status === 'approved') msg = `Candidate${nameStr} approved.`
      else if (status === 'deleted') msg = `Candidate${nameStr} deleted (bad/duplicate — do not add to scene).`
      else msg = `Candidate${nameStr} skipped.`
    } else {
      const parts: string[] = []
      if (approvedLabels.length) parts.push(`Approved: ${approvedLabels.map(l => `"${l}"`).join(', ')}.`)
      if (skippedLabels.length)  parts.push(`Skipped: ${skippedLabels.map(l => `"${l}"`).join(', ')}.`)
      if (deletedLabels.length)  parts.push(`Deleted (bad/duplicate — do not add to scene): ${deletedLabels.map(l => `"${l}"`).join(', ')}.`)
      msg = parts.length ? parts.join(' ') : 'All candidates resolved.'
    }
    pendingCandidatesRef.current = new Map()
    handleSend(msg)
  }

  async function handleApproveCandidate(messageId: string, itemId: string, label?: string) {
    await approveCandidate(itemId, label)
    markStale('candidates', 'scene-list', 'scene-data')
    const existing = pendingCandidatesRef.current.get(messageId)
    pendingCandidatesRef.current.set(messageId, { status: 'approved', label: label ?? existing?.label })
    checkAndSendCandidateSummary()
  }

  async function handleSkipCandidate(messageId: string, itemId: string, label?: string) {
    await skipCandidate(itemId, label)
    markStale('candidates')
    const existing = pendingCandidatesRef.current.get(messageId)
    pendingCandidatesRef.current.set(messageId, { status: 'skipped', label: label ?? existing?.label })
    checkAndSendCandidateSummary()
  }

  async function handleDeleteCandidate(messageId: string, itemId: string) {
    await deleteCandidate(itemId)
    markStale('candidates')
    const existing = pendingCandidatesRef.current.get(messageId)
    pendingCandidatesRef.current.set(messageId, { status: 'deleted', label: existing?.label })
    checkAndSendCandidateSummary()
  }

  async function handleSend(text: string) {
    // Interrupt any in-progress stream
    if (abortControllerRef.current) {
      abortControllerRef.current.abort()
      abortControllerRef.current = null
      stopCharTimer()
      flushCharBuffer()
      activeAidRef.current = null
    }

    const controller = new AbortController()
    abortControllerRef.current = controller

    pendingCandidatesRef.current = new Map()
    setError(null)
    setWarnings([])

    const userMsg: Message = { id: uid(), role: 'user', content: text }
    setMessages((prev) => [...prev, userMsg])
    setLoading(true)

    // Use the full Anthropic-format history (includes tool calls/results) if available,
    // otherwise fall back to reconstructing plain-text history from display messages
    const history: HistoryMessage[] = fullHistoryRef.current.length > 0
      ? fullHistoryRef.current
      : (() => {
          const h: HistoryMessage[] = []
          for (const m of messagesRef.current) {
            if (m.role === 'status' || m.role === 'candidate' || m.role === 'open_dates') continue
            const last = h[h.length - 1]
            if (last && last.role === 'assistant' && m.role === 'assistant') {
              last.content += '\n\n' + m.content
            } else {
              h.push({ role: m.role as 'user' | 'assistant', content: m.content })
            }
          }
          return h
        })()

    // Track current assistant message and status message being built up
    let currentAssistantId: string | null = null
    let currentStatusId: string | null = null
    let lastAssistantId: string | null = null

    try {
      const { saved, warnings: w, messages } = await streamChat(
        text,
        history,
        (delta) => {
          // Text is flowing — end any open status group
          currentStatusId = null
          if (!currentAssistantId) {
            const aid = uid()
            currentAssistantId = aid
            lastAssistantId = aid
            activeAidRef.current = aid
            setMessages((prev) => [...prev, { id: aid, role: 'assistant', content: '', saved: [] }])
            // Start char-by-char release timer
            if (!charTimerRef.current) {
              charTimerRef.current = setInterval(() => {
                const buf = charBufferRef.current
                if (!buf || !activeAidRef.current) return
                const n = buf.length > 80 ? 8 : 3
                const chunk = buf.slice(0, n)
                charBufferRef.current = buf.slice(n)
                const id = activeAidRef.current
                setMessages((prev) => prev.map((m) => m.id === id ? { ...m, content: m.content + chunk } : m))
              }, 16)
            }
          }
          charBufferRef.current += delta
        },
        (line) => {
          // Tool status — flush buffer and end current assistant segment
          flushCharBuffer()
          activeAidRef.current = null
          currentAssistantId = null
          if (!currentStatusId) {
            const sid = uid()
            currentStatusId = sid
            setMessages((prev) => [...prev, { id: sid, role: 'status', content: line }])
          } else {
            const sid = currentStatusId
            setMessages((prev) =>
              prev.map((m) => m.id === sid ? { ...m, content: m.content + '\n' + line } : m)
            )
          }
        },
        (candidate) => {
          // Candidate proposed — inject an inline candidate card
          flushCharBuffer()
          activeAidRef.current = null
          currentAssistantId = null
          currentStatusId = null
          const cid = uid()
          pendingCandidatesRef.current.set(cid, { status: 'pending', label: candidate.label })
          markStale('candidates')
          setMessages((prev) => [...prev, { id: cid, role: 'candidate', content: '', candidate }])
        },
        (openDates) => {
          // Open dates result — inject an inline card without loading data into context
          flushCharBuffer()
          activeAidRef.current = null
          currentAssistantId = null
          currentStatusId = null
          const oid = uid()
          setMessages((prev) => [...prev, { id: oid, role: 'open_dates', content: '', openDates }])
        },
        controller.signal,
        (candidate) => {
          // Candidate updated — patch the existing card in the chat and refresh the candidates tab
          setMessages((prev) =>
            prev.map((m) =>
              m.role === 'candidate' && m.candidate?.item_id === candidate.item_id
                ? { ...m, candidate }
                : m
            )
          )
          markStale('candidates')
        },
      )
      if (lastAssistantId) {
        const aid = lastAssistantId
        setMessages((prev) => prev.map((m) => m.id === aid ? { ...m, saved } : m))
      }
      if (messages?.length) fullHistoryRef.current = messages
      if (w?.length) setWarnings(w)
    } catch (e) {
      if (e instanceof DOMException && e.name === 'AbortError') return
      const msg = e instanceof Error ? e.message : 'Something went wrong'
      if (msg.includes('401')) { clearToken(); setToken_(null); return }
      setError(msg)
    } finally {
      if (abortControllerRef.current === controller) {
        abortControllerRef.current = null
        stopCharTimer()
        flushCharBuffer()
        activeAidRef.current = null
        setLoading(false)
      }
    }
  }

  function navigateTo(p: Page) {
    setPage(p)
    setVisited((prev) => prev.has(p) ? prev : new Set([...prev, p]))
    if (staleRef.current.has(p)) {
      staleRef.current.delete(p)
      setRefreshKeys(prev => ({ ...prev, [p]: (prev[p] ?? 0) + 1 }))
    }
  }

  function handleShortcut(message: string) {
    navigateTo('chat')
    setNavOpen(false)
    handleSend(message)
  }

  if (!token && !authError) {
    return <LoginPage onLogin={(t) => setToken_(t)} />
  }

  if (authError === '403') {
    return (
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100dvh', background: 'var(--bg)', fontFamily: 'var(--font)' }}>
        <div style={{ textAlign: 'center', color: 'var(--text-muted)' }}>
          <div style={{ fontSize: '1.1rem', fontWeight: 600, marginBottom: '0.5rem', color: 'var(--text)' }}>Unauthorized</div>
        </div>
      </div>
    )
  }

  if (!verified) return <AppLoadingView />

  return (
    <div
      style={{
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
        background: 'var(--bg)',
        maxWidth: '640px',
        margin: '0 auto',
      }}
    >
      <SideNav page={page} open={navOpen} onNavigate={navigateTo} onClose={() => setNavOpen(false)} />
      <NavBar onMenuOpen={() => setNavOpen(true)} />

      {/* Chat — kept mounted to preserve history */}
      <div style={{ display: page === 'chat' ? 'contents' : 'none' }}>
        <ChatWindow
          messages={messages}
          loading={loading}
          onApproveCandidate={handleApproveCandidate}
          onSkipCandidate={handleSkipCandidate}
          onDeleteCandidate={handleDeleteCandidate}
        />

        {error && (
          <div style={{ padding: '8px 16px', background: '#450a0a', color: '#fca5a5', fontSize: '13px', borderTop: '1px solid #7f1d1d' }}>
            {error}
          </div>
        )}

        {warnings.map((w, i) => (
          <div key={i} style={{ padding: '8px 16px', background: '#422006', color: '#fdba74', fontSize: '13px', borderTop: '1px solid #7c2d12', display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: '8px' }}>
            <span>⚠ {w}</span>
            <button onClick={() => setWarnings((prev) => prev.filter((_, j) => j !== i))} style={{ flexShrink: 0, color: '#fdba74', fontSize: '16px', lineHeight: 1, opacity: 0.7 }}>✕</button>
          </div>
        ))}

        <MessageInput
          onSend={handleSend}
          historySizeBytes={historySizeBytes}
          onStop={() => {
            if (abortControllerRef.current) {
              abortControllerRef.current.abort()
              abortControllerRef.current = null
              stopCharTimer()
              flushCharBuffer()
              activeAidRef.current = null
              setLoading(false)
            }
          }}
          disabled={loading}
        />
      </div>

      {visited.has('candidates') && (
        <div style={{ display: page === 'candidates' ? 'contents' : 'none' }}>
          <CandidatesPage
            refreshKey={refreshKeys['candidates']}
            onApprove={() => markStale('scene-list', 'scene-data')}
          />
        </div>
      )}

      {visited.has('shortcuts') && (
        <div style={{ display: page === 'shortcuts' ? 'contents' : 'none' }}>
          <ShortcutsPage onShortcut={handleShortcut} />
        </div>
      )}

      {visited.has('scene-list') && (
        <div style={{ display: page === 'scene-list' ? 'contents' : 'none' }}>
          <SceneListPage refreshKey={refreshKeys['scene-list']} />
        </div>
      )}

      {visited.has('scene-map') && (
        <div style={{ display: page === 'scene-map' ? 'contents' : 'none' }}>
          <SceneMapPage />
        </div>
      )}

      {visited.has('scene-data') && (
        <div style={{ display: page === 'scene-data' ? 'contents' : 'none' }}>
          <SheetTablePage targetId="kumu_elements" refreshKey={refreshKeys['scene-data']} />
        </div>
      )}

      {visited.has('shows') && (
        <div style={{ display: page === 'shows' ? 'contents' : 'none' }}>
          <SheetTablePage targetId="shows" />
        </div>
      )}

      {visited.has('fans') && (
        <div style={{ display: page === 'fans' ? 'contents' : 'none' }}>
          <SheetTablePage targetId="fans" />
        </div>
      )}

      {visited.has('jontha-info') && (
        <div style={{ display: page === 'jontha-info' ? 'contents' : 'none' }}>
          <DocPage targetId="jontha_info" />
        </div>
      )}

      {visited.has('mod-log') && (
        <div style={{ display: page === 'mod-log' ? 'contents' : 'none' }}>
          <ModLogPage />
        </div>
      )}

      {visited.has('sync') && (
        <div style={{ display: page === 'sync' ? 'contents' : 'none' }}>
          <SyncPage />
        </div>
      )}
    </div>
  )
}
